import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import userService from '../../services/UserService.js';
import salaryService from '../../services/SalaryService.js';
import attendanceService from '../../services/AttendanceService.js';
import AlertModal from '../common/AlertModal.js';
import Alert from '../common/Alert.js';
import { Bell, LogOut, Menu, Search, Settings } from 'lucide-react';
import UserAvatar from './UserAvatar.js';
import { useSidebarMobileOptional } from '../../context/SidebarMobileContext.js';
import Button from './Button.js';
import LanguageSwitcher from './LanguageSwitcher.js';
import { hasAttendanceAccess, canManualAttendance } from '../../utils/networkMode.js';
import AppBrandLogo from './AppBrandLogo.js';
import dqLogo from '../../static/images/dq_logo.png';
import { useCompanyLogo } from '../../hooks/useCompanyLogo.js';
import { getCurrentDevicePosition } from '../../utils/deviceLocation.js';
import HeaderShipmentSearch from './HeaderShipmentSearch.js';
import ShipmentDetailModal from '../shipment/ShipmentDetailModal.js';
import alertService from '../../services/AlertService.js';
import useAppHotkeys from '../../hooks/useAppHotkeys.js';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp.js';
import NavJumpPalette from './NavJumpPalette.js';
import DeliverGcModal from './DeliverGcModal.js';
import { flattenNavDestinations, formatNavGoChord } from '../../utils/sidebarMenu.js';
import { openDeliverForShipment, shipmentIdFromViewPath } from '../../utils/deliverShortcut.js';
import axiosInstance from '../../config/axios.js';
import { saveAgentSwitch } from '../../utils/agentSwitchBoot.js';

const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading } = useAuth();
  const sidebarMobile = useSidebarMobileOptional();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [fullUserData, setFullUserData] = useState(null);
  const [currentSalary, setCurrentSalary] = useState(null);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [headerProfilePhoto, setHeaderProfilePhoto] = useState(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const companyLogo = useCompanyLogo(user);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [workDuration, setWorkDuration] = useState(0);
  const [loginTime, setLoginTime] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [headerShipmentId, setHeaderShipmentId] = useState(null);
  const [deliverModalOpen, setDeliverModalOpen] = useState(false);
  const [deliverLoading, setDeliverLoading] = useState(false);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const [showJumpPalette, setShowJumpPalette] = useState(false);
  const canSearchShipments = ['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin'].includes(user?.role);
  const profileUserId = user?.actor?.user_id || user?.user_id;
  const [switchOptions, setSwitchOptions] = useState(null);
  const [switchSaving, setSwitchSaving] = useState(false);

  useEffect(() => {
    if (showProfileModal && profileUserId && !fullUserData) {
      fetchFullUserData();
      fetchCurrentSalary();
    }
  }, [showProfileModal, profileUserId]);

  useEffect(() => {
    if (!showProfileModal) return undefined;
    const actorRole = user?.actor?.role || user?.role;
    if (actorRole !== 'Agent' || !user?.company_features?.agent_switch) {
      setSwitchOptions(null);
      return undefined;
    }
    let active = true;
    axiosInstance.get('/auth/agent-switch').then((response) => {
      if (active) setSwitchOptions(response.data || null);
    }).catch(() => {
      if (active) setSwitchOptions(null);
    });
    return () => {
      active = false;
    };
  }, [showProfileModal, user?.actor?.role, user?.role, user?.company_features?.agent_switch]);

  const handleAgentSwitch = async (targetUserId, displayName, phase) => {
    if (switchSaving) return;
    setSwitchSaving(true);
    try {
      await saveAgentSwitch(targetUserId, displayName, phase);
    } catch (err) {
      setSwitchSaving(false);
      Alert.error(err?.response?.data?.error || 'Could not switch agent');
    }
  };

  useEffect(() => {
    if (user?.user_id && !headerProfilePhoto) {
      userService.getById(user.user_id).then((response) => {
        setHeaderProfilePhoto(response.data?.profile_photo_url || null);
      }).catch(() => {});
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (user?.user_id && hasAttendanceAccess(user)) {
      checkActiveAttendance();
    }
  }, [user?.user_id, user?.network_mode]);

  useEffect(() => {
    if (!user?.user_id) return;
    alertService.getUnreadCount(user.user_id)
      .then((res) => setUnreadCount(res?.unread_count ?? 0))
      .catch(() => {});
  }, [user?.user_id]);

  useEffect(() => {
    let interval;
    if (isLoggedIn && loginTime) {
      interval = setInterval(() => {
        const now = new Date();
        const login = new Date(loginTime);
        const duration = Math.floor((now - login) / 1000);
        setWorkDuration(duration);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoggedIn, loginTime]);

  const handleAlertClick = () => setShowAlertModal(true);

  const handleCloseAlertModal = () => {
    setShowAlertModal(false);
  };

  const getCurrentPosition = () => getCurrentDevicePosition();

  const checkActiveAttendance = async () => {
    try {
      const response = await attendanceService.getActiveStatus();
      if (response.is_active && response.data) {
        setIsLoggedIn(true);
        setLoginTime(response.data.login_time);
        setAttendanceDate(response.data.attendance_date);
      } else {
        setIsLoggedIn(false);
        setLoginTime(null);
        setAttendanceDate(null);
        setWorkDuration(0);
      }
    } catch (err) {
      console.error('Failed to check attendance status:', err);
    }
  };

  const handleAttendanceLogin = async () => {
    try {
      setAttendanceLoading(true);
      Alert.info('Getting your location...');
      const position = await getCurrentPosition();
      const response = await attendanceService.loginAttendance(position.latitude, position.longitude);
      setIsLoggedIn(true);
      setLoginTime(response.data.login_time);
      setAttendanceDate(response.data.attendance_date);
      setWorkDuration(0);
      Alert.success('Attendance logged in successfully!');
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to login attendance';
      Alert.error(errorMessage);
      if (err.response?.status === 400 && err.response?.data?.active_attendance) {
        checkActiveAttendance();
      }
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleAttendanceLogout = async () => {
    const today = new Date().toISOString().split('T')[0];
    const loginDate = attendanceDate ? new Date(attendanceDate).toISOString().split('T')[0] : today;

    let confirmMessage = `You have worked for ${formatDuration(workDuration)}. Are you sure you want to logout?`;

    if (loginDate !== today) {
      const loginDateFormatted = new Date(attendanceDate).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
      confirmMessage = `You logged in on ${loginDateFormatted}. You have worked for ${formatDuration(workDuration)}. Are you sure you want to logout now?`;
    }

    const confirmed = await Alert.confirm(confirmMessage, 'Logout Attendance');

    if (confirmed) {
      try {
        setAttendanceLoading(true);
        Alert.info('Getting your location...');
        const position = await getCurrentPosition();
        const response = await attendanceService.logoutAttendance(position.latitude, position.longitude);
        setIsLoggedIn(false);
        setLoginTime(null);
        setAttendanceDate(null);
        setWorkDuration(0);
        Alert.success(`Logged out successfully! Total work time: ${response.data.work_hours_formatted}`);
      } catch (err) {
        Alert.error(err.response?.data?.error || err.message || 'Failed to logout attendance');
      } finally {
        setAttendanceLoading(false);
      }
    }
  };

  const getLoginDisplayDate = () => {
    if (!attendanceDate) return '';
    const today = new Date().toISOString().split('T')[0];
    const loginDate = new Date(attendanceDate).toISOString().split('T')[0];
    if (loginDate !== today) {
      return new Date(attendanceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    }
    return '';
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const formatDurationShort = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const fetchFullUserData = async () => {
    try {
      setLoadingUserData(true);
      const response = await userService.getById(profileUserId);
      setFullUserData(response.data);
    } catch (err) {
      console.error('Failed to load user details:', err);
    } finally {
      setLoadingUserData(false);
    }
  };

  const fetchCurrentSalary = async () => {
    try {
      const currentDate = new Date();
      const currentMonth = currentDate.toLocaleString('en-US', { month: 'long' }).toLowerCase();
      const currentYear = currentDate.getFullYear();
      const response = await salaryService.getAll(1, 10, {
        user_id: profileUserId, month: currentMonth, year: currentYear
      });
      if (response.data && response.data.length > 0) {
        setCurrentSalary(response.data[0]);
      }
    } catch (err) {
      console.error('Failed to load salary data:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const getProfileImageUrl = (userData = null) => {
    const target = userData || user;
    return target?.profile_photo_url || null;
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getSalaryStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-700',
      credited: 'bg-green-100 text-green-700',
      processing: 'bg-blue-100 text-blue-700',
      hold: 'bg-orange-100 text-orange-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-700';
  };

  const showAttendanceClockIn = canManualAttendance(user);
  const showAttendanceControls = showAttendanceClockIn || (isLoggedIn && hasAttendanceAccess(user));
  const goShortcutGroup = useMemo(() => {
    if (!user) return [];
    const items = flattenNavDestinations(user)
      .filter((row) => row.primary && row.goKey)
      .map((row) => ({
        keys: formatNavGoChord(row.goKey),
        action: row.label || t(row.labelKey),
      }));
    if (!items.length) return [];
    return [{ id: 'goto', title: 'Go to page (press A, then the letter within 1 second)', items }];
  }, [user, t]);

  useAppHotkeys({
    onModX: async ({ pathname }) => {
      const onStatus = String(pathname || '').includes('/update-status') || String(pathname || '').endsWith('/status-update');
      if (onStatus) {
        const selected = Number(window.__jabbarStatusSelectedCount || 0);
        if (selected > 0) {
          window.dispatchEvent(new CustomEvent('jabbar-set-delivered'));
          return;
        }
      }
      const viewShipmentId = shipmentIdFromViewPath(pathname);
      if (viewShipmentId) {
        const result = await openDeliverForShipment(viewShipmentId);
        if (result?.navigateTo) {
          setHeaderShipmentId(null);
          navigate(result.navigateTo);
        }
        return;
      }
      if (headerShipmentId) {
        const result = await openDeliverForShipment(headerShipmentId);
        if (result?.navigateTo) {
          setHeaderShipmentId(null);
          navigate(result.navigateTo);
        }
        return;
      }
      setDeliverModalOpen(true);
    },
    onToggleHelp: (next) => {
      if (next === false) setShowShortcutHelp(false);
      else {
        setShowJumpPalette(false);
        setShowShortcutHelp((open) => !open);
      }
    },
    onFocusSearch: () => {
      if (!canSearchShipments) return;
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (isMobile) setMobileSearchOpen(true);
      window.setTimeout(() => {
        document.querySelector('[data-header-shipment-search]')?.focus();
      }, isMobile ? 50 : 0);
    },
    onToggleAttendance: () => {
      if (!canManualAttendance(user) || attendanceLoading) return;
      if (isLoggedIn) handleAttendanceLogout();
      else handleAttendanceLogin();
    },
    onOpenJumpPalette: () => {
      setShowShortcutHelp(false);
      setShowJumpPalette(true);
    },
  });

  return (
    <>
      <header className="bg-white sticky top-0 z-50 isolate border-b border-gray-200">
        <div className="flex items-center justify-between gap-1.5 sm:gap-3 px-2 sm:px-6 py-1.5 min-h-[64px] sm:min-h-[76px]">
          {/* Left: menu + full logo. The slot has a real size so the mark scales instead of clipping. */}
          <div className="flex items-center gap-1.5 sm:gap-2.5 min-w-0 flex-1">
            {sidebarMobile && (
              <Button
                variant="ghost"
                onClick={() =>
                  sidebarMobile.isOpen
                    ? sidebarMobile.closeSidebar()
                    : sidebarMobile.openSidebar()
                }
                className="lg:hidden !p-1.5 sm:!p-2 -ml-0.5 flex-shrink-0 rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300"
                aria-label={sidebarMobile.isOpen ? t('header.closeMenu') : 'Open menu'}
              >
                <Menu className="w-5 h-5 text-gray-700" />
              </Button>
            )}
            <div className="h-11 min-w-0 flex-1 max-w-[11.5rem] sm:h-14 sm:max-w-[16rem] md:h-16 md:max-w-[18.5rem]">
              <AppBrandLogo
                companyLogo={companyLogo}
                variant="header"
                onClick={() => window.location.reload()}
              />
            </div>
          </div>
          {/* Right: search + actions */}
          <div className="flex items-center gap-0.5 sm:gap-1.5 flex-shrink-0 ml-auto">
            {loading ? (
              <div className="w-7 h-7 sm:w-8 sm:h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            ) : user ? (
              <>
                {canSearchShipments && (
                  <div className="hidden md:block">
                    <HeaderShipmentSearch onSelectShipment={setHeaderShipmentId} />
                  </div>
                )}
                {canSearchShipments && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setMobileSearchOpen((open) => !open)}
                    className="md:hidden relative !p-1.5 sm:!p-2 rounded-lg"
                    title={t('dashboard.searchGcOrMobile')}
                    aria-label={t('dashboard.searchGcOrMobile')}
                    aria-expanded={mobileSearchOpen}
                  >
                    <Search className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-gray-600" />
                  </Button>
                )}
                {/* Alert bell */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAlertClick}
                  className="relative !p-1.5 sm:!p-2 rounded-lg"
                  title={t('header.notifications')}
                >
                  <Bell className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-gray-600" />
                  {unreadCount > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-semibold text-white bg-red-500 rounded-full ring-2 ring-white leading-none"
                      aria-label={t('header.unreadNotifications')}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Button>

                {/* Attendance section */}
                {showAttendanceControls && (
                  <div className="flex items-center gap-0.5 sm:gap-2 sm:border-l sm:border-gray-200 sm:pl-2.5">
                    {isLoggedIn ? (
                      <div className="flex items-center gap-0.5 sm:gap-2">
                        <div className="relative flex-shrink-0 hidden sm:block">
                          <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                          <div className="absolute inset-0 w-2.5 h-2.5 bg-green-500 rounded-full opacity-75 animate-ping" />
                        </div>
                        <div className="hidden sm:flex flex-col items-start">
                          <span className="text-xs font-mono text-gray-900 font-semibold">
                            {formatDuration(workDuration)}
                          </span>
                          {getLoginDisplayDate() && (
                            <span className="text-[10px] text-gray-500">{getLoginDisplayDate()}</span>
                          )}
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={handleAttendanceLogout}
                          disabled={attendanceLoading}
                          className="rounded-md text-xs !px-1.5 sm:!px-2 !py-1"
                          title={`${t('header.logoutAttendance')} (${formatDuration(workDuration)})`}
                        >
                          {attendanceLoading ? '...' : (
                            <>
                              <span className="sm:hidden">{formatDurationShort(workDuration)}</span>
                              <span className="hidden sm:inline">{t('header.clockOut')}</span>
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleAttendanceLogin}
                        disabled={attendanceLoading}
                        className="rounded-md text-xs !p-1.5 sm:!px-2.5 sm:!py-1.5"
                        title={t('header.loginAttendance')}
                      >
                        {attendanceLoading ? '...' : (
                          <>
                            <div className="w-2 h-2 bg-white rounded-full flex-shrink-0" />
                            <span className="hidden sm:inline">{t('header.clockIn')}</span>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}

                {/* Profile */}
                <Button
                  variant="ghost"
                  onClick={() => setShowProfileModal(true)}
                  className="hover:bg-gray-50 rounded-lg !p-1 sm:!p-1.5 sm:border-l sm:border-gray-200 sm:pl-2.5 sm:ml-0.5"
                >
                  <UserAvatar
                    src={headerProfilePhoto || getProfileImageUrl()}
                    alt={user.name}
                    size="sm"
                    className="border-2 border-gray-200"
                  />
                  <div className="hidden lg:block text-left">
                    <p className="text-sm font-semibold text-gray-800 leading-tight">{user.name}</p>
                    <p className="text-xs text-gray-500">
                      {user.acting_as ? `Signed in as ${user.actor?.name || 'your account'}` : user.role}
                    </p>
                  </div>
                </Button>

                {/* System logout */}
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="!p-1.5 sm:!p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg flex-shrink-0"
                  title={t('header.logout')}
                >
                  <LogOut className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                </Button>
              </>
            ) : null}
            <img
              src={dqLogo}
              alt="DQ"
              className="h-9 w-9 sm:h-11 sm:w-11 object-contain shrink-0 ml-1 sm:ml-2"
            />
          </div>
        </div>
        {canSearchShipments && mobileSearchOpen && (
          <div className="md:hidden px-2 pb-2">
            <HeaderShipmentSearch
              variant="mobile"
              onSelectShipment={(id) => {
                setHeaderShipmentId(id);
                setMobileSearchOpen(false);
              }}
            />
          </div>
        )}
      </header>

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
          <div className="bg-white w-full sm:max-w-4xl sm:rounded-xl shadow-2xl min-h-screen sm:min-h-0 sm:my-8 max-h-screen sm:max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">{t('common.profile')}</h2>
              <div className="flex items-center gap-1">
                <LanguageSwitcher variant="compact" />
                <Button
                  variant="ghost"
                  onClick={() => setShowProfileModal(false)}
                  className="p-2 rounded-full"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </Button>
              </div>
            </div>

            {loadingUserData ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4" />
                  <p className="text-gray-500 text-sm">{t('common.loading')}</p>
                </div>
              </div>
            ) : fullUserData ? (
              <>
                {/* Profile Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-8">
                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                    <UserAvatar
                      src={getProfileImageUrl(fullUserData)}
                      alt={fullUserData.name}
                      size="xl"
                      className="border-4 border-white/30 bg-white"
                      iconClassName="text-gray-400"
                    />
                    <div className="flex-1 text-center sm:text-left">
                      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">{fullUserData.name}</h2>
                      <p className="text-blue-200 text-sm sm:text-base mb-3">{fullUserData.role_name}</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        fullUserData.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {capitalizeStatus(fullUserData.status)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="p-4 sm:p-8 space-y-6">
                  {currentSalary && (
                    <ProfileSection title={`Current Salary Status (${currentSalary.month_display} ${currentSalary.year})`}>
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 rounded-xl border border-blue-100">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Net Salary</label>
                            <p className="text-lg font-bold text-blue-600">{formatCurrency(currentSalary.net_salary)}</p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Payment Status</label>
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getSalaryStatusBadge(currentSalary.status)}`}>
                              {currentSalary.status.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Payment Date</label>
                            <p className="text-sm text-gray-800 font-medium">{formatDate(currentSalary.payment_date)}</p>
                          </div>
                        </div>
                      </div>
                    </ProfileSection>
                  )}

                  <ProfileSection title={t('common.basicInfo')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ProfileField label="Username" value={fullUserData.username || 'N/A'} />
                      <ProfileField label="Full Name" value={fullUserData.name} />
                      <ProfileField label="Email" value={fullUserData.email} />
                      <ProfileField label="Company" value={fullUserData.company_name ? `${fullUserData.company_name} (${fullUserData.company_id || 'N/A'})` : 'N/A'} />
                      <ProfileField label="Date of Birth" value={formatDate(fullUserData.dob)} />
                    </div>
                  </ProfileSection>

                  <ProfileSection title={t('common.contactInfo')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ProfileField label="Contact Number" value={fullUserData.contact} />
                      <ProfileField label="City" value={fullUserData.city} />
                      <div className="sm:col-span-2">
                        <ProfileField label="Location/Address" value={fullUserData.location} />
                      </div>
                    </div>
                  </ProfileSection>

                  <ProfileSection title={t('common.documentInfo')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ProfileField label="Document Type" value={fullUserData.document_type ? fullUserData.document_type.replace(/_/g, ' ').toUpperCase() : 'N/A'} />
                      <ProfileField label="Document Number" value={fullUserData.document_number || 'N/A'} />
                    </div>
                  </ProfileSection>

                  {user?.acting_as && (
                    <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <p className="text-sm text-blue-900">
                        Working as <span className="font-semibold">{user.acting_as.name}</span>. Signed in as {user.actor?.name}.
                      </p>
                      <Button
                        variant="secondary"
                        onClick={() => handleAgentSwitch(null, user.actor?.name || 'your account', 'own')}
                        disabled={switchSaving}
                      >
                        My own account
                      </Button>
                    </div>
                  )}

                  {switchOptions?.enabled && (switchOptions.targets || []).length > 0 && (
                    <ProfileSection title="Work as">
                      <div className="space-y-2">
                        <label className="flex items-start gap-2 text-sm text-gray-800">
                          <input
                            type="radio"
                            name="agent-switch"
                            checked={!user?.acting_as}
                            disabled={switchSaving}
                            onChange={() => {
                              if (!user?.acting_as) return;
                              handleAgentSwitch(null, user?.actor?.name || 'your account', 'own');
                            }}
                          />
                          <span>
                            My own account
                            <span className="block text-xs text-gray-500">{user?.actor?.name} ({user?.actor?.user_id})</span>
                          </span>
                        </label>
                        {switchOptions.targets.map((agent) => (
                          <label key={agent.user_id} className="flex items-start gap-2 text-sm text-gray-800">
                            <input
                              type="radio"
                              name="agent-switch"
                              checked={user?.acting_as?.user_id === agent.user_id}
                              disabled={switchSaving}
                              onChange={() => {
                                if (user?.acting_as?.user_id === agent.user_id) return;
                                handleAgentSwitch(agent.user_id, agent.name, 'switch');
                              }}
                            />
                            <span>
                              {agent.name}
                              <span className="block text-xs text-gray-500">{agent.user_id}{agent.location ? ` · ${agent.location}` : ''}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </ProfileSection>
                  )}

                  <ProfileSection title={t('common.roleAndStatus')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ProfileField label="Role" value={fullUserData.role_name} />
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          fullUserData.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {capitalizeStatus(fullUserData.status)}
                        </span>
                      </div>
                    </div>
                  </ProfileSection>

                  <ProfileSection title={t('common.systemInfo')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ProfileField label="User ID" value={fullUserData.user_id} />
                      <ProfileField label="Created By" value={fullUserData.created_by || 'N/A'} />
                      <ProfileField label="Created At" value={formatDate(fullUserData.created_at)} />
                      <ProfileField label="Last Updated" value={formatDate(fullUserData.updated_at)} />
                    </div>
                  </ProfileSection>
                </div>

                {/* Modal Footer */}
                <div className="sticky bottom-0 bg-gray-50 px-4 sm:px-8 py-4 border-t flex flex-col sm:flex-row gap-3">
                  {['Admin', 'System Admin'].includes(user?.role) && (
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={() => {
                        setShowProfileModal(false);
                        navigate('/company-settings');
                      }}
                      className="rounded-lg flex items-center justify-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Company Settings
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => setShowProfileModal(false)}
                    className="rounded-lg"
                  >
                    {t('common.close')}
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      <KeyboardShortcutsHelp
        open={showShortcutHelp}
        onClose={() => setShowShortcutHelp(false)}
        user={user}
        pathname={location.pathname}
        extraGroups={goShortcutGroup}
      />
      <NavJumpPalette
        open={showJumpPalette}
        onClose={() => setShowJumpPalette(false)}
        user={user}
      />
      <AlertModal
        isOpen={showAlertModal}
        onClose={handleCloseAlertModal}
        userId={user?.user_id}
        onUnreadChange={setUnreadCount}
      />
      <ShipmentDetailModal
        shipmentId={headerShipmentId}
        onClose={() => setHeaderShipmentId(null)}
      />
      <DeliverGcModal
        isOpen={deliverModalOpen}
        loading={deliverLoading}
        onClose={() => setDeliverModalOpen(false)}
        onSubmit={async (id) => {
          setDeliverLoading(true);
          try {
            const result = await openDeliverForShipment(id);
            if (result?.navigateTo) {
              setDeliverModalOpen(false);
              navigate(result.navigateTo);
            }
          } finally {
            setDeliverLoading(false);
          }
        }}
      />
    </>
  );
};

function ProfileSection({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b-2 border-blue-500">
        {title}
      </h3>
      {children}
    </div>
  );
}

function ProfileField({ label, value }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <p className="text-sm text-gray-800 font-medium break-all">{value}</p>
    </div>
  );
}

export default Header;
